import type { FieldPolicy } from "@apollo/client";

/** Structural shape of the cached `finpubPrimaryFields` listing. The
 * generated query type is what actually flows through; only the fields
 * the merge touches are named here. */
type PrimaryFieldsListing = {
  items: ReadonlyArray<unknown>;
  pagination: { total: number };
};

/** Offset-merging cache policy for `Query.finpubPrimaryFields`.
 *
 * `keyArgs` folds every page of one catalogue (same arena + search) into a
 * single cache entry, and `merge` places each page at its offset. Writes
 * are therefore idempotent: concurrent paginators (e.g. two mounted
 * `useAllPrimaryFields` instances fetching the same page) cannot duplicate
 * items, and a page-one refetch refreshes rows in place instead of
 * discarding the rest of the listing.
 *
 * Cache-bypassing consumers (the Add Field Picker datasource uses
 * `no-cache`) are unaffected. Test caches must install this policy before
 * exercising pagination — see `useAllPrimaryFields.test.tsx`.
 */
export const finpubPrimaryFieldsFieldPolicy: FieldPolicy<PrimaryFieldsListing> = {
  keyArgs: ["arenaId", "search"],
  merge(existing, incoming, { args }) {
    if (!existing) return incoming;
    const offset = typeof args?.offset === "number" ? args.offset : 0;
    // A changed total means the catalogue itself changed, so rows cached
    // under the old total are unreliable. Keep only those before this page;
    // the shorter listing (items < total) makes paginating consumers
    // re-walk the dropped range instead of serving stale rows inside the
    // new total as a "complete" catalogue.
    const items =
      existing.pagination.total === incoming.pagination.total
        ? [...existing.items]
        : existing.items.slice(0, offset);
    incoming.items.forEach((item, index) => {
      items[offset + index] = item;
    });
    return { ...incoming, items: items.slice(0, incoming.pagination.total) };
  },
};
